import classNames from "classnames";
import { useFormik } from "formik";
import { useAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { PiEnvelopeSimple } from "react-icons/pi";
import { useSelector } from "react-redux";
import { FileInput, Input, Password } from "rizzui";
import { toast } from "sonner";
import * as yup from 'yup';
import { formsSubmitAtom } from "../../../atom/atom";
import DeletePopover from "../../../components/buttons/DeletePopover";
import AsyncImg from "../../../components/extras/AsyncImg";
import PlaceholderImage from "../../../components/extras/PlaceholderImage";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import PhoneInput from "../../../components/forms/PhoneInputWrapper";
import TabNavInside, { MenuItemInside } from "../../../components/navigation/TabNavInside";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useRoles from "../../../hooks/api-calls/useRoles";
import useFetch from "../../../hooks/useFetch";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { User } from "../../../redux/authSlice";
import { RootState } from "../../../redux/store";
import { OrganisationService } from "../../../services/company/organisationService";
import { UserService } from "../../../services/user/userService";
import { es } from "date-fns/locale";
import { DatePicker } from "../../../components/forms/DatePicker";
import moment from "moment";

interface UserFormProps {
    userSelected: any;
    profileImg?: any;
    formRef?: any;
    setOpenModal: any;
    refetch: () => void;
}

const editSchema = yup.object({
    name: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo').required('Campo obligatorio'),
    lastName: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo'),
    email: yup.string().email('Email no válido').required('Campo obligatorio'),
    password: yup.string().min(8, 'Contraseña de al menos 8 caracteres').max(30, 'Contraseña menor de 30 caracteres').matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/, 'La contraseña debe contener al menos una letra mayúscula, una minúscula y un número')
        .when('isCreateMode', {
            is: (val: boolean) => val == true,
            then: (schema: yup.Schema) => schema.required('Campo obligatorio'),
            otherwise: (schema: yup.Schema) => schema.nullable()
        })
        .nullable(),
    passwordConfirm: yup.string()
        .when('password', {
            is: (val: string | null | undefined) => val !== null && val !== undefined && val.length > 0,
            then: (schema: yup.Schema) => schema.oneOf([yup.ref('password'), ''], 'Las contraseñas no coinciden'),
            otherwise: (schema: yup.Schema) => schema.nullable(),
        })
        .when('isCreateMode', {
            is: (val: boolean) => val == true,
            then: (schema: yup.Schema) => schema.required('Campo obligatorio'),
            otherwise: (schema: yup.Schema) => schema.nullable()
        })
        .nullable(),
    profileImg: yup.mixed(),
    roleId: yup.string().required('Campo obligatorio'),
    dni: yup.string().required('Campo obligatorio'),
    address: yup.string().nullable(),
    phone: yup.string().nullable(),
    monthlySalary: yup.number().nullable(),
});

const superAdminEditSchema = editSchema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

const schema = yup.object({
    name: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo').required('Campo obligatorio'),
    lastName: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo'),
    email: yup.string().email('Email no válido'),
    profileImg: yup.mixed(),
    roleId: yup.string().required('Campo obligatorio'),
    dni: yup.string().required('Campo obligatorio'),
    address: yup.string().nullable(),
    phone: yup.string().nullable(),
    contractType: yup.string().nullable(),
    schedule: yup.string().nullable(),
    socialSecurityNumber: yup.string().nullable(),
    birthDate: yup.string().nullable(),
    monthlySalary: yup.number().nullable(),
});

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))



const UserForm: React.FC<UserFormProps> = ({ userSelected, profileImg, formRef, setOpenModal, refetch }) => {

    // STATES

    const mode = userSelected ? 'Editar' : 'Crear';
    const [selectedImage, setSelectedImage] = useState<any>(null);
    const [selectedView, setSelectedView] = useState<string>('mainInfo');
    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const [isSearched, setIsSearched] = useState<boolean>(false);

    // HOOKS

    const service = new UserService();
    const { user } = useSelector((state: RootState) => state.auth);
    const company = user?.companyId;
    const { userCan } = usePrivilege();
    const superAdmin = userCan('list', 'company');
    const { getRolesList, fetchRoles } = useRoles({ companyId: superAdmin ? null : company });
    const { handleErrors } = useHandleErrors();

    // MENU ITEMS DINÁMICOS

    const menuItems: MenuItemInside[] = [
        {
            label: 'Información General',
            id: 'mainInfo',
            permission: {
                group: 'user',
                action: 'edit',
            },
            fieldsError: ['name', 'lastName', 'email', 'dni', 'roleId'],
        },
        ...(userSelected !== null ? [{
            label: 'Información Opcional',
            id: 'optionalInfo',
            permission: {
                group: 'user' as const,
                action: 'edit',
            },
            fieldsError: ['password', 'passwordConfirm']
        }] : [])
    ];

    // FUNCTIONS

    // -----------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER COMPAÑIAS
     * @EN GET COMPANIES
     */
    // -----------------------------------------------------------------------------------------------------------
    const [companyData] = useFetch(useCallback(async () => {
        if (!superAdmin) return;
        const companyService = new OrganisationService();
        let filters = {
            limit: 999999,
        };
        const response = await companyService.listOrganisations(filters);
        return response.getResponseData() as User;
    }, [superAdmin]));
    // -----------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER COMPAÑIAS PARA EL SELECT
     * @EN GET COMPANIES FOR THE SELECT
     */
    // -----------------------------------------------------------------------------------------------------------
    const getCompanyOptions = () => {
        let companyOptions = companyData?.data?.map((data: { id: string; name: string; }) => ({
            value: data.id,
            label: data.name,
        }));

        return companyOptions

    }
    // -----------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJADOR DEL CAMBIO DE IMAGEN DEL USUARIO
     * @EN HANDLER FOR USER IMAGE CHANGE
     * 
     * @param event
     */
    // -----------------------------------------------------------------------------------------------------------
    const handleImageUpload = async (event: React.ChangeEvent<any>) => {
        if (userSelected?.id === undefined) return;

        const file = event.target.files[0];
        const reader = new FileReader();
        reader.onload = () => {
            setSelectedImage(reader.result);
        };
        reader.readAsDataURL(file);

        try {
            const response = await service.editUserImg(userSelected?.id || '', file);
            const responseData = response.getResponseData();
            if (responseData.success) {
                setTimeout(() => {
                    toast.success('Imagen de perfil actualizada');
                }, 100);
            } else {
                toast.error(responseData.message);
                setSelectedImage(null);
            }
        } catch (error: any) {
            toast.error("Formato de imagen incorrecto");
            setSelectedImage(null);
        }
    };
    // -----------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJADOR DEL BORRADO DE IMAGEN DEL USUARIO
     * @EN HANDLER FOR USER IMAGE DELETION
     */
    // -----------------------------------------------------------------------------------------------------------
    const deleteImage = async () => {
        if (userSelected?.id === undefined) return;
        try {
            const response = await service.deleteUserImg(userSelected?.id || '');
            const responseData = response.getResponseData();
            if (responseData.success) {
                setSelectedImage(null);
                window.location.reload();
            }
        } catch (error: any) {
            toast.error("Error al eliminar la imagen");
        }
    };
    // -----------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL ENVIO DEL FORMULARIO DE EDICION
     * @EN HANDLE THE SUBMIT OF THE EDIT FORM
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleUpdate = async (values: any) => {
        try {
            if (values.password !== "" && values.passwordConfirm !== "") {
                const result = await service.changePassword(values.id, values.password, values.passwordConfirm);

                if (result.getResponseData().success) {
                    toast.success("Contraseña cambiada correctamente");
                } else {
                    toast.error("Error al cambiar la contraseña");
                }
            }

            const response = (await service.editUser(values)).getResponseData();

            if (response.success) {
                toast.success("Usuario editado correctamente");
                refetch();
                setOpenModal(false);
            } else {
                handleErrors(response);
                setIsSubmitting(false);
                return;
            }
        } catch (error: any) {
            toast.error("Error al editar el usuario");
            setIsSubmitting(false);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL ENVIO DEL FORMULARIO DE CREACION
     * @EN HANDLE THE SUBMIT OF THE CREATE FORM
     *
     * @param values
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = (await service.createUser(values)).getResponseData();

            if (response.success) {
                setOpenModal(false);
                refetch();
                toast.success("Usuario creado correctamente");
                setIsSubmitting(false);
            } else {
                handleErrors(response);
                setIsSubmitting(false);
                return;
            }
        } catch (error: any) {
            toast.error("Error al crear el usuario");
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // FORMIK
    const formik = useFormik({
        initialValues: {
            id: userSelected?.id ?? '',
            company: userSelected?.userRoles[0]?.company?.id ?? '',
            roleId: userSelected?.userRoles[0]?.role?.id ?? '',
            name: userSelected?.name ?? '',
            lastName: userSelected?.lastName ?? '',
            email: userSelected?.email ?? '',
            password: '',
            passwordConfirm: '',
            profileImg: userSelected?.profileImg?.id ?? undefined,
            dni: userSelected?.dni ?? '',
            address: userSelected?.address ?? '',
            phone: userSelected?.phone ?? '',
            monthlySalary: userSelected?.monthlySalary ?? null,
            contractType: userSelected?.contractType ?? '',
            schedule: userSelected?.schedule ?? '',
            socialSecurityNumber: userSelected?.socialSecurityNumber ?? '',
            birthDate: userSelected?.birthDate?.date ?? '',
        },
        validationSchema: userSelected ? superAdmin ? superAdminEditSchema : editSchema : superAdmin ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (userSelected) {
                handleUpdate(values);
            } else {
                handleCreate(values);
            }
        },
    });

    // USE EFFECTS

    useEffect(() => {
        fetchRoles({
            companyId: formik.values.company,
        });
    }, [formik.values.company]);

    useEffect(() => {
        if (!formik.values.company && getCompanyOptions()?.length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompanyOptions()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompanyOptions]);

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                {userSelected !== null && (
                    <TabNavInside menuItems={menuItems} selectedView={selectedView} setSelectedView={setSelectedView} errors={formik.errors} />
                )}
                <div className="mb-10 grid gap-4 divide-y-2 divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11 font-medium">
                    {
                        selectedView === 'mainInfo' && (
                            <div className={'md:col-span-12'}>
                                <div className='md:grid md:grid-cols-12 gap-4'>
                                    <RequiredInput
                                        id='name'
                                        label="Nombre"
                                        className='md:col-span-12 mb-2'
                                        formik={formik}
                                        error={formik.errors.name}
                                    />

                                    <RequiredInput
                                        id='lastName'
                                        required={false}
                                        label="Apellidos"
                                        className='md:col-span-12 mb-2'
                                        formik={formik}
                                        error={formik.errors.lastName}
                                    />               

                                    <RequiredInput
                                        id='dni'
                                        label="DNI"
                                        className='md:col-span-12 mb-2'
                                        formik={formik}
                                        error={formik.errors.dni}
                                    />

                                    <RequiredInput
                                        required={false}
                                        id='address'
                                        label="Dirección"
                                        className='md:col-span-12 mb-2'
                                        formik={formik}
                                        error={formik.errors.address}
                                    />

                                    <PhoneInput
                                        id='phone'
                                        label='Teléfono'
                                        className="md:col-span-12 mb-2"
                                        onBlur={formik.handleBlur}
                                        onChange={e => { formik.setFieldValue('phone', e) }}
                                        value={formik.values.phone}
                                        error={formik.errors.phone as string}
                                        required={false}
                                    />

                                    <RequiredInput
                                        required={false}
                                        id='monthlySalary'
                                        label="Salario semanal (€)"
                                        type="number"
                                        className='md:col-span-12 mb-2'
                                        suffix="€/semana"
                                        formik={formik}
                                        error={formik.errors.monthlySalary}
                                    />

                                    <RequiredInput
                                        required={true}
                                        id='email'
                                        type="email"
                                        label="Correo Electrónico"
                                        prefix={<PiEnvelopeSimple className="h-6 w-6 text-gray-500" />}
                                        className='md:col-span-12 mb-2'
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                    />

                                    <RequiredInput
                                        required={false}
                                        id='contractType'
                                        label="Tipo de contrato"
                                        className='md:col-span-12 mb-2'
                                        formik={formik}
                                        error={formik.errors.contractType}
                                    />

                                    <RequiredInput
                                        required={false}
                                        id='schedule'
                                        label="Horario"
                                        className='md:col-span-12 mb-2'
                                        formik={formik}
                                        error={formik.errors.schedule}
                                    />

                                    <RequiredInput
                                        required={false}
                                        id='socialSecurityNumber'
                                        label="Número de seguridad social"
                                        className='md:col-span-12 mb-2'
                                        formik={formik}
                                        error={formik.errors.socialSecurityNumber}
                                    />

                                     <div className="md:col-span-12">
                                        <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700 mb-2">
                                            Fecha de nacimiento <span className="text-red-500">*</span>
                                        </label>
                                        <DatePicker
                                            monthsShown={1}
                                            placeholderText="Selecciona una fecha"
                                            inputProps={{
                                                inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.errors.birthDate ? 'border-red-500' : ''}`,
                                            }}
                                            className="col-span-12 mb-2"
                                            selected={formik.values?.birthDate ? moment(formik.values.birthDate).toDate() : null}
                                            onChange={(date: any) => {
                                                if (date) {
                                                    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
                                                    formik.setFieldValue('birthDate', normalizedDate);
                                                } else {
                                                    formik.setFieldValue('birthDate', null);
                                                }
                                            }}
                                            locale={es}
                                            minDate={moment().subtract(100, 'years').toDate()}
                                            maxDate={moment().toDate()}
                                            popperPlacement="bottom-start"
                                            popperClassName="!z-[9999]"
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                            yearDropdownItemNumber={100}
                                        />
                                        {formik.errors.birthDate && (
                                            <p className="text-red-500 text-sm mt-1 break-words">{formik.errors.birthDate?.toString()}</p>
                                        )}
                                    </div>

                                    {superAdmin && (
                                        <CustomSelect
                                            required
                                            isSearchable
                                            id={'company'}
                                            label="Compañia"
                                            containerClassName='md:col-span-12 mb-2'
                                            value={
                                                formik.values.company ?
                                                { value: formik.values.company, label: getCompanyOptions()?.find((g: any) => g.value === formik.values.company)?.label } :
                                                getCompanyOptions()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                                            }
                                            options={getCompanyOptions()}
                                            onChange={
                                                (e: any) => { formik.setFieldValue('company', e?.value) }
                                            }
                                            error={formik.errors.company}
                                        />
                                    )}


                                    <CustomSelect
                                        required
                                        isSearchable
                                        id={'roleId'}
                                        label="Rol"
                                        containerClassName='md:col-span-12'
                                        value={{ value: formik.values.roleId, label: getRolesList()?.find((g: any) => g.value === formik.values.roleId)?.label }}
                                        options={getRolesList()}
                                        onChange={(e: any) => { formik.setFieldValue('roleId', e?.value) }}
                                        error={formik.errors.roleId}
                                        display={userCan('list', 'role')}
                                    />

                                </div>
                            </div>
                        )
                    }
                    {
                        selectedView === 'optionalInfo' && userSelected && (
                            <div className={'md:col-span-12'}>
                                <div className='md:grid md:grid-cols-12 gap-4'>
                                    {userSelected && (
                                        <>
                                            <Password
                                                id='password'
                                                label="Contraseña"
                                                className="[&>label>span]:font-medium md:col-span-12"
                                                inputClassName="text-sm"
                                                onChange={formik.handleChange}
                                                value={formik.values.password}
                                                error={formik.errors.password}
                                            />
                                            <Password
                                                id='passwordConfirm'
                                                label="Confirmar contraseña"
                                                className="[&>label>span]:font-medium md:col-span-12"
                                                inputClassName="text-sm"
                                                onChange={formik.handleChange}
                                                value={formik.values.passwordConfirm}
                                                error={formik.errors.passwordConfirm}
                                            />
                                        </>
                                    )}
                                    <div className={classNames('md:col-span-12', { 'hidden': mode === 'Crear' })}>
                                        {selectedImage
                                            ? <div style={{ height: "200px", width: "200px" }}>
                                                <img src={selectedImage} alt="selected" className='mx-auto d-block img-fluid rounded object-cover w-[220px] h-[220px]' />
                                            </div>
                                            : profileImg
                                                ? <AsyncImg id={profileImg.id} isBackground className="mx-auto d-block img-fluid rounded w-[220px] h-[220px] object-cover" />
                                                : <PlaceholderImage width={200} height={200} className='mx-auto d-block img-fluid rounded' />
                                        }
                                        <div className={'flex justify-start items-center mt-4'}>
                                            <FileInput onChange={(e: React.ChangeEvent<any>) => handleImageUpload(e)} autoComplete='photo' placeholder={"Cambiar imagen"} className='me-3' />
                                            <DeletePopover
                                                title={`Eliminar imagen`}
                                                description={`¿Estás seguro de que deseas eliminar la imagen?`}
                                                onDelete={deleteImage}
                                                size={32}
                                                className={classNames({ 'hidden': !profileImg?.id })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </div>
            </form>
        </div>
    )
}

export default UserForm;